/**
 * 確認ダイアログ経由のフォーム送信（data 属性駆動。user-admin 05_ui.md §3.4）
 *
 * マークアップ規約:
 *   <form data-lms-confirm-submit="confirm-make-admin"
 *         data-lms-confirm-when="#make-admin:checked"
 *         data-lms-confirm-when-changed="input[name=email]"
 *         data-lms-confirm-fill="name:#name, email:#email">
 *
 *   <div id="confirm-make-admin" class="lms-overlay" data-lms-modal hidden>
 *     <span data-lms-fill-slot="name"></span>
 *     <button data-lms-confirm-ok>実行</button>
 *   </div>
 *
 * data-lms-confirm-when: マッチする要素が存在するときだけ確認する。
 * data-lms-confirm-when-changed: 対象入力の値が data-initial から変わっていれば確認する（OR 判定）。
 * data-lms-confirm-fill: モーダル内の [data-lms-fill-slot] へ差し込む値の取得元。
 *
 * 実装時の訂正（2026-07-22）: hx-boost されたフォームでは htmx が submit イベントの
 * リスナーをフォーム要素自身（イベントの target）に bubble フェーズで直接
 * addEventListener している。DOM のイベント発火順は「target 自身に付いたリスナーは
 * capture/bubble を問わず登録順で実行される」ため、確認用リスナーを同じフォームに
 * capture:true で付け直しても htmx より先に実行される保証はない。実測でも、htmx が
 * main.ts の DOMContentLoaded ハンドラより先に自身の初期化を走らせるケースがあり、
 * 確認ダイアログを表示しつつも htmx が先に AJAX 送信を発行してしまっていた
 * （確認せずに管理者権限を付与してしまう High バグとして顕在化）。
 * 対策として、フォーム個別ではなく document に 1 つだけ capture フェーズの委譲
 * リスナーを登録する。capture フェーズは target フェーズより必ず先に実行されるため、
 * 確認が必要な場合はここで stopPropagation / stopImmediatePropagation して、
 * フォーム自身に付いた htmx のリスナーへイベントを一切渡さない。
 *
 * 代替案として `hx-boost="false"` の付与も検討したが不採用にした。確認ダイアログ経由の
 * フォームは「確認不要（通常の変更なし）なら通常送信」「確認必要なら OK 後に
 * form.submit() で送信」という分岐があり、hx-boost を外すと確認不要な通常時の送信まで
 * 常にフルページ遷移になり、同じ画面の他の boosted フォームとの体験差が生まれる。
 * イベント interception 側（本ファイル）で解決したほうが影響範囲を最小化できるため、
 * こちらを採用する。
 *
 * 追記（2026-07-27・fix-admin-promotion-button / issue #4）:
 * 本ファイルは当初「フォームの submit イベント起点」の確認だけを想定していたが、
 * ユーザー管理の「管理者を追加」は候補選択モーダルのボタンクリックから確認モーダルを
 * 直接開くため submit イベントが発火せず、OK ボタンが永久に不活性になっていた
 * （POST が 1 本も出ない無言の失敗として issue #4 で顕在化）。
 * 対策として結線ヘルパー armConfirmModal() を公開し、ボタンクリック起点の経路からも
 * 同じ pendingForms + ensureOkHandlerBound に合流できるようにした。
 * OK 結線の実装箇所は引き続き本ファイルの 1 箇所に保つこと（結線が 2 箇所に分裂すると
 * 二重 submit の温床になる。design.md D1 代替案 (b) の不採用理由）。
 */
import { openModal } from "./modal.js";

/** モーダル内の [data-lms-fill-slot] に値を差し込む。キーが無いスロットは空文字にする。 */
export function fillSlots(modal: HTMLElement, values: Record<string, string>): void {
  modal.querySelectorAll<HTMLElement>("[data-lms-fill-slot]").forEach((slot) => {
    const key = slot.dataset["lmsFillSlot"];
    slot.textContent = (key && values[key]) ?? "";
  });
}

function readValue(el: Element): string {
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
    return el.value;
  }
  return el.textContent ?? "";
}

function parseFillSpec(spec: string): Array<[string, string]> {
  return spec
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .map((part) => {
      const idx = part.indexOf(":");
      return [part.slice(0, idx).trim(), part.slice(idx + 1).trim()] as [string, string];
    });
}

function collectFillValues(form: HTMLFormElement): Record<string, string> {
  const spec = form.dataset["lmsConfirmFill"];
  if (!spec) return {};
  const values: Record<string, string> = {};
  for (const [key, selector] of parseFillSpec(spec)) {
    const el = document.querySelector(selector);
    values[key] = el ? readValue(el) : "";
  }
  return values;
}

function isWhenSatisfied(form: HTMLFormElement): boolean {
  const selector = form.dataset["lmsConfirmWhen"];
  if (!selector) return false;
  return document.querySelector(selector) !== null;
}

function isWhenChangedSatisfied(form: HTMLFormElement): boolean {
  const selector = form.dataset["lmsConfirmWhenChanged"];
  if (!selector) return false;
  const targets = Array.from(
    form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(selector),
  );
  return targets.some((el) => {
    const initial = el.dataset["initial"];
    if (initial === undefined) return false;
    return el.value !== initial;
  });
}

function needsConfirmation(form: HTMLFormElement): boolean {
  const hasWhen = form.dataset["lmsConfirmWhen"] !== undefined;
  const hasWhenChanged = form.dataset["lmsConfirmWhenChanged"] !== undefined;
  if (!hasWhen && !hasWhenChanged) return true;
  return isWhenSatisfied(form) || isWhenChangedSatisfied(form);
}

/** 確認待ちのフォーム（モーダル id → フォーム）。[data-lms-confirm-ok] の委譲先を切り替える。 */
const pendingForms = new Map<string, HTMLFormElement>();

function ensureOkHandlerBound(modal: HTMLElement, modalId: string): void {
  if (modal.dataset["lmsConfirmOkReady"] === "1") return;
  modal.dataset["lmsConfirmOkReady"] = "1";

  modal.querySelectorAll<HTMLElement>("[data-lms-confirm-ok]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const form = pendingForms.get(modalId);
      if (form) form.submit();
    });
  });
}

/**
 * 確認モーダルを「ボタンクリック起点」で開く経路（remote-list.ts 等）から使う結線ヘルパー。
 *
 * submit イベント起点のインターセプタ（bindDelegatedInterceptor）が内部で行っている
 * 「送信対象フォームの登録」＋「OK ボタンへの click 結線」を、そのまま外部へ公開したもの。
 * これにより両経路が pendingForms + ensureOkHandlerBound という同一の結線機構に合流し、
 * OK 結線の実装箇所を本ファイル 1 箇所に保てる（fix-admin-promotion-button design.md D1）。
 *
 * 呼び出し側は「確認モーダルを openModal する直前に毎回」呼ぶこと。
 * pendingForms は modal.id をキーに上書きされるため、候補の選び直しや htmx 部分更新後の
 * 再操作でも常に最新のフォーム参照が送信対象になる（同 D2）。
 * ensureOkHandlerBound は要素の dataset ガードを持つので、毎回呼んでも二重結線しない。
 *
 * @param modal 確認モーダル要素（id 必須。id が空の要素は pendingForms のキーを作れないため無視する）
 * @param form  OK 押下時に submit() する対象のフォーム
 */
export function armConfirmModal(modal: HTMLElement, form: HTMLFormElement): void {
  if (!modal.id) return;
  pendingForms.set(modal.id, form);
  ensureOkHandlerBound(modal, modal.id);
}

/** document 単位の委譲リスナーは 1 個だけ登録すればよい（htmx:afterSettle 後の再初期化でも二重登録しない） */
let delegatedInterceptorBound = false;

function bindDelegatedInterceptor(): void {
  if (delegatedInterceptorBound) return;
  delegatedInterceptorBound = true;

  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      const modalId = form.dataset["lmsConfirmSubmit"];
      if (!modalId) return;
      if (!needsConfirmation(form)) return;

      const modal = document.getElementById(modalId);
      if (!modal) return;

      // capture フェーズで完全に止める。htmx の submit リスナーはフォーム自身（target
      // フェーズ）に付いているため、target に到達する前にここで食い止める必要がある。
      // stopImmediatePropagation() は stopPropagation() の効果を含むが、意図を明示する
      // ため両方呼ぶ（document 上の他の capture リスナーにも渡さない）。
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      fillSlots(modal, collectFillValues(form));
      pendingForms.set(modalId, form);
      ensureOkHandlerBound(modal, modalId);
      openModal(modal);
    },
    { capture: true },
  );
}

/**
 * scope 引数は他の init* 関数とのシグネチャ統一のために残すが、実際の割り込みは
 * document 単位の委譲リスナー 1 個で完結するため未使用（htmx:afterSettle での
 * 再初期化呼び出しに対しても安全に多重呼び出しできる）。
 */
export function initConfirmSubmit(_scope: ParentNode = document): void {
  bindDelegatedInterceptor();
}
